export type Direction = "game-to-host" | "host-to-game";
export type Envelope = "none" | "flat" | "nested";
export type Silence =
  | "not-a-request"
  | "never-silent"
  | "always-silent"
  | "silent-when-invalid"
  | "silent-when-no-identity";

export interface FieldSpec {
  name: string;
  type: string;
  optional?: boolean;
  sample?: unknown;
}

export interface MessageSpec {
  direction: Direction;
  envelope: Envelope;
  handled: boolean;
  fields: readonly FieldSpec[];
  reply: readonly string[];
  silence: Silence;
}

export const GRANT_REASONS = ["LEVEL_COMPLETE", "ACHIEVEMENT", "DAILY_OBJECTIVE", "IDLE_REWARD"] as const;
export const COIN_REJECTION_CODES = ["DENIED", "RATE_LIMITED", "INVALID"] as const;
export const COIN_GRANT_STATUSES = ["GRANTED", "LOCKED"] as const;
export const SCORE_MODES = ["add", "overwrite", "best"] as const;

export const LOGOUT_MESSAGE =
  "You've logged out but don't worry, you can pick up where you left off and your score is saved in your account.";
export const TOKEN_REFRESH_FAILED_ERROR = "Failed to refresh token";

export type GrantReason = (typeof GRANT_REASONS)[number];
export type CoinRejectionCode = (typeof COIN_REJECTION_CODES)[number];
export type CoinGrantStatus = (typeof COIN_GRANT_STATUSES)[number];
export type ScoreMode = (typeof SCORE_MODES)[number];

export function isScoreMode(v: unknown): v is ScoreMode {
  return typeof v === "string" && (SCORE_MODES as readonly string[]).includes(v);
}

export interface ScoreWriteNearbyRow {
  rank?: number | null;
  score?: number;
  displayName?: string | null;
  avatarUrl?: string | null;
  identityId?: string | null;
}

export interface ScoreWriteOutcome {
  persisted: boolean;
  score?: number;
  rank?: number;
  /** True when this write set a new all-time best for the player. */
  isNewBest?: boolean;
  /** previousRank − currentRank (positive = moved up). */
  rankChange?: number;
  previousRank?: number | null;
  currentRank?: number | null;
  bestScore?: number | null;
  /** Rows around the player after the write (±3). */
  nearby?: ScoreWriteNearbyRow[];
}

export const PROTOCOL_VERSION_FIELD: FieldSpec = {
  name: "protocolVersion",
  type: "string",
  optional: true,
  sample: "1",
};

export const MESSAGES = {
  GAME_LOADED: {
    direction: "game-to-host",
    envelope: "none",
    handled: true,
    fields: [],
    reply: ["PARENT_DATA", "ANON_ID"],
    silence: "silent-when-no-identity",
  },
  REFRESH_TOKEN: {
    direction: "game-to-host",
    envelope: "none",
    handled: true,
    fields: [],
    reply: ["TOKEN_REFRESHED", "TOKEN_REFRESH_FAILED"],
    silence: "never-silent",
  },
  GAME_OVER: {
    direction: "game-to-host",
    envelope: "flat",
    handled: true,
    fields: [
      { name: "roundId", type: "string", sample: "round-42" },
      { name: "score", type: "number", sample: 1250 },
      { name: "durationMs", type: "number", sample: 94000 },
      { name: "scoreMode", type: "'add' | 'overwrite' | 'best'", optional: true },
      { name: "metadata", type: "Record<string, unknown>", optional: true },
    ],
    reply: ["GAME_OVER_ACK"],
    silence: "silent-when-invalid",
  },
  LEVEL_COMPLETE: {
    direction: "game-to-host",
    envelope: "flat",
    handled: true,
    fields: [
      { name: "roundId", type: "string", sample: "round-5" },
      { name: "level", type: "number | string", sample: 3 },
      { name: "score", type: "number", optional: true },
      { name: "durationMs", type: "number", optional: true },
      { name: "scoreMode", type: "'add' | 'overwrite' | 'best'", optional: true },
      { name: "metadata", type: "Record<string, unknown>", optional: true },
    ],
    reply: ["LEVEL_COMPLETE_ACK"],
    silence: "silent-when-invalid",
  },
  GRANT_COINS: {
    direction: "game-to-host",
    envelope: "flat",
    handled: true,
    fields: [
      {
        name: "reason",
        type: "'LEVEL_COMPLETE' | 'ACHIEVEMENT' | 'DAILY_OBJECTIVE' | 'IDLE_REWARD'",
        sample: "LEVEL_COMPLETE",
      },
      { name: "amount", type: "number", sample: 50 },
      { name: "eventId", type: "string", optional: true },
    ],
    reply: ["COINS_GRANTED", "COINS_REJECTED"],
    silence: "never-silent",
  },
  GAME_STARTED: {
    direction: "game-to-host",
    envelope: "flat",
    handled: false,
    fields: [{ name: "roundId", type: "string", sample: "round-1" }],
    reply: [],
    silence: "always-silent",
  },
  REDIRECT_TO_LOGIN: {
    direction: "game-to-host",
    envelope: "none",
    handled: true,
    fields: [],
    reply: [],
    silence: "always-silent",
  },
  CHILD_DATA_UPDATE: {
    direction: "game-to-host",
    envelope: "nested",
    handled: true,
    fields: [{ name: "currency", type: "number", sample: 500 }],
    reply: [],
    silence: "always-silent",
  },
  DEBUG_LOG: {
    direction: "game-to-host",
    envelope: "nested",
    handled: true,
    fields: [
      { name: "timestamp", type: "string", sample: "2026-07-30T00:00:00.000Z" },
      { name: "message", type: "unknown", sample: "debug message" },
    ],
    reply: [],
    silence: "always-silent",
  },
  ERROR_LOG: {
    direction: "game-to-host",
    envelope: "nested",
    handled: true,
    fields: [
      { name: "context", type: "string", sample: "load" },
      { name: "message", type: "string", sample: "error message" },
      { name: "timestamp", type: "string", sample: "2026-07-30T00:00:00.000Z" },
    ],
    reply: [],
    silence: "always-silent",
  },
  PARENT_DATA: {
    direction: "host-to-game",
    envelope: "nested",
    handled: true,
    fields: [
      { name: "authToken", type: "string | null", sample: "test-auth-token" },
      { name: "refreshToken", type: "string | null", sample: "test-refresh-token" },
      { name: "anonymousId", type: "string | null", sample: "anon-test-id" },
      { name: "eightpointId", type: "string | null", sample: "test-user-id" },
    ],
    reply: [],
    silence: "not-a-request",
  },
  ANON_ID: {
    direction: "host-to-game",
    envelope: "nested",
    handled: true,
    fields: [
      { name: "anonymousId", type: "string", sample: "anon-test-id" },
      { name: "eightpointId", type: "string | null", sample: "test-user-id" },
    ],
    reply: [],
    silence: "not-a-request",
  },
  TOKEN_REFRESHED: {
    direction: "host-to-game",
    envelope: "nested",
    handled: true,
    fields: [
      { name: "authToken", type: "string", sample: "test-auth-token" },
      { name: "refreshToken", type: "string | null", sample: "test-refresh-token" },
      { name: "success", type: "true", sample: true },
    ],
    reply: [],
    silence: "not-a-request",
  },
  TOKEN_REFRESH_FAILED: {
    direction: "host-to-game",
    envelope: "nested",
    handled: true,
    fields: [
      { name: "success", type: "false", sample: false },
      { name: "error", type: "string", sample: TOKEN_REFRESH_FAILED_ERROR },
    ],
    reply: [],
    silence: "not-a-request",
  },
  USER_LOGGEDOUT: {
    direction: "host-to-game",
    envelope: "nested",
    handled: true,
    fields: [{ name: "message", type: "string", sample: LOGOUT_MESSAGE }],
    reply: [],
    silence: "not-a-request",
  },
  GAME_OVER_ACK: {
    direction: "host-to-game",
    envelope: "flat",
    handled: true,
    fields: [
      { name: "roundId", type: "string", sample: "round-placeholder" },
      { name: "persisted", type: "boolean", optional: true, sample: true },
      { name: "score", type: "number", optional: true, sample: 1250 },
      { name: "rank", type: "number", optional: true, sample: 7 },
    ],
    reply: [],
    silence: "not-a-request",
  },
  LEVEL_COMPLETE_ACK: {
    direction: "host-to-game",
    envelope: "flat",
    handled: true,
    fields: [
      { name: "roundId", type: "string", sample: "round-placeholder" },
      { name: "level", type: "number | string", sample: 1 },
      { name: "persisted", type: "boolean", optional: true, sample: true },
      { name: "score", type: "number", optional: true, sample: 1250 },
      { name: "rank", type: "number", optional: true, sample: 7 },
    ],
    reply: [],
    silence: "not-a-request",
  },
  COINS_GRANTED: {
    direction: "host-to-game",
    envelope: "flat",
    handled: true,
    fields: [
      {
        name: "reason",
        type: "'LEVEL_COMPLETE' | 'ACHIEVEMENT' | 'DAILY_OBJECTIVE' | 'IDLE_REWARD'",
        sample: "LEVEL_COMPLETE",
      },
      { name: "amount", type: "number", sample: 10 },
      { name: "newBalance", type: "number", sample: 0 },
      { name: "status", type: "'GRANTED' | 'LOCKED'", sample: "GRANTED" },
      { name: "replayed", type: "boolean", sample: false },
    ],
    reply: [],
    silence: "not-a-request",
  },
  COINS_REJECTED: {
    direction: "host-to-game",
    envelope: "flat",
    handled: true,
    fields: [
      {
        name: "reason",
        type: "'LEVEL_COMPLETE' | 'ACHIEVEMENT' | 'DAILY_OBJECTIVE' | 'IDLE_REWARD'",
        sample: "LEVEL_COMPLETE",
      },
      { name: "code", type: "'DENIED' | 'RATE_LIMITED' | 'INVALID'", sample: "DENIED" },
      { name: "retryable", type: "boolean", sample: false },
    ],
    reply: [],
    silence: "not-a-request",
  },
} as const satisfies Record<string, MessageSpec>;

export type MessageType = keyof typeof MESSAGES;
export const MESSAGE_TYPES = Object.keys(MESSAGES) as MessageType[];
export const GAME_TO_HOST = MESSAGE_TYPES.filter((t) => MESSAGES[t].direction === "game-to-host");
export const HOST_TO_GAME = MESSAGE_TYPES.filter((t) => MESSAGES[t].direction === "host-to-game");

export function sampleMessage(type: MessageType): Record<string, unknown> {
  const spec: MessageSpec = MESSAGES[type];
  const fields: Record<string, unknown> = {};
  for (const f of spec.fields) {
    if (f.sample === undefined) continue;
    fields[f.name] = f.sample;
  }
  if (spec.envelope === "nested") return { type, payload: fields };
  return { type, ...fields };
}

export function originFromSrc(src: string): string | null {
  try {
    return new URL(src).origin;
  } catch {
    return null;
  }
}

export function isExpectedOrigin(origin: string | null, expected: string | null): boolean {
  if (!expected || expected === "*") return false;
  if (origin === "null") return false;
  return origin === expected;
}

export function isKnownMessageType(v: unknown): v is MessageType {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(MESSAGES, v);
}

export function messageTypeOf(data: unknown): MessageType | null {
  if (typeof data !== "object" || data === null) return null;
  const t = (data as Record<string, unknown>).type;
  return isKnownMessageType(t) ? t : null;
}

export function isGrantReason(v: unknown): v is GrantReason {
  return typeof v === "string" && (GRANT_REASONS as readonly string[]).includes(v);
}

export interface GameOverFields {
  roundId: string;
  score: number;
  durationMs: number;
  scoreMode?: ScoreMode;
  metadata?: Record<string, unknown>;
}

export interface LevelCompleteFields {
  roundId: string;
  level: number | string;
  score?: number;
  durationMs?: number;
  scoreMode?: ScoreMode;
  metadata?: Record<string, unknown>;
}

export interface GrantCoinsFields {
  reason: GrantReason;
  amount: number;
  eventId?: string;
}

function asMetadata(value: unknown): Record<string, unknown> | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;
}

export function parseGameOver(data: unknown): GameOverFields | null {
  const raw = data as { payload?: unknown } | null | undefined;
  const source = (raw?.payload ?? data) as
    | { roundId?: unknown; score?: unknown; durationMs?: unknown; scoreMode?: unknown; metadata?: unknown }
    | null
    | undefined;
  const { roundId, score, durationMs, scoreMode, metadata } = source ?? {};
  const valid =
    typeof roundId === "string" &&
    roundId.length > 0 &&
    typeof score === "number" &&
    Number.isFinite(score) &&
    typeof durationMs === "number" &&
    Number.isFinite(durationMs);
  if (!valid) return null;
  return { roundId, score, durationMs, scoreMode: isScoreMode(scoreMode) ? scoreMode : undefined, metadata: asMetadata(metadata) };
}

export function parseLevelComplete(data: unknown): LevelCompleteFields | null {
  const raw = data as { payload?: unknown } | null | undefined;
  const source = (raw?.payload ?? data) as
    | { roundId?: unknown; level?: unknown; score?: unknown; durationMs?: unknown; scoreMode?: unknown; metadata?: unknown }
    | null
    | undefined;
  const { roundId, level, score, durationMs, scoreMode, metadata } = source ?? {};
  const valid = typeof roundId === "string" && roundId.length > 0 && (typeof level === "number" || typeof level === "string");
  if (!valid) return null;
  return {
    roundId,
    level,
    score: typeof score === "number" && Number.isFinite(score) ? score : undefined,
    durationMs: typeof durationMs === "number" && Number.isFinite(durationMs) ? durationMs : undefined,
    scoreMode: isScoreMode(scoreMode) ? scoreMode : undefined,
    metadata: asMetadata(metadata),
  };
}

export function parseGrantCoins(data: unknown): GrantCoinsFields | null {
  const raw = data as { payload?: unknown } | null | undefined;
  const source = (raw?.payload ?? data) as { reason?: unknown; amount?: unknown; eventId?: unknown } | null | undefined;
  const { reason, amount, eventId } = source ?? {};
  if (!isGrantReason(reason)) return null;
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) return null;
  return { reason, amount, eventId: typeof eventId === "string" ? eventId : undefined };
}

export const build = {
  parentData(payload: {
    authToken: string | null | undefined;
    refreshToken: string | null | undefined;
    anonymousId: string | null | undefined;
    eightpointId: string | null | undefined;
  }): Record<string, unknown> {
    return {
      type: "PARENT_DATA",
      payload: {
        authToken: payload.authToken ?? null,
        refreshToken: payload.refreshToken ?? null,
        anonymousId: payload.anonymousId ?? null,
        eightpointId: payload.eightpointId ?? null,
      },
    };
  },
  anonId(payload: { anonymousId: string; eightpointId: string | null | undefined }): Record<string, unknown> {
    return {
      type: "ANON_ID",
      payload: { anonymousId: payload.anonymousId, eightpointId: payload.eightpointId ?? null },
    };
  },
  userLoggedOut(): Record<string, unknown> {
    return { type: "USER_LOGGEDOUT", payload: { message: LOGOUT_MESSAGE } };
  },
  tokenRefreshed(authToken: string, refreshToken: string | null | undefined): Record<string, unknown> {
    return { type: "TOKEN_REFRESHED", payload: { authToken, refreshToken: refreshToken ?? null, success: true } };
  },
  tokenRefreshFailed(error: string = TOKEN_REFRESH_FAILED_ERROR): Record<string, unknown> {
    return { type: "TOKEN_REFRESH_FAILED", payload: { success: false, error } };
  },
  gameOverAck(roundId: string, outcome?: ScoreWriteOutcome): Record<string, unknown> {
    const ack: Record<string, unknown> = { type: "GAME_OVER_ACK", roundId };
    if (outcome) {
      ack.persisted = outcome.persisted;
      if (outcome.score !== undefined) ack.score = outcome.score;
      if (outcome.rank !== undefined) ack.rank = outcome.rank;
    }
    return ack;
  },
  levelCompleteAck(roundId: string, level: number | string, outcome?: ScoreWriteOutcome): Record<string, unknown> {
    const ack: Record<string, unknown> = { type: "LEVEL_COMPLETE_ACK", roundId, level };
    if (outcome) {
      ack.persisted = outcome.persisted;
      if (outcome.score !== undefined) ack.score = outcome.score;
      if (outcome.rank !== undefined) ack.rank = outcome.rank;
    }
    return ack;
  },
  coinsGranted(payload: {
    reason: GrantReason;
    amount: number;
    newBalance: number;
    status: CoinGrantStatus;
    replayed: boolean;
  }): Record<string, unknown> {
    return { type: "COINS_GRANTED", ...payload };
  },
  coinsRejected(payload: { reason: GrantReason; code: CoinRejectionCode; retryable: boolean }): Record<string, unknown> {
    return { type: "COINS_REJECTED", ...payload };
  },
};
